import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/content';
import { BottomSheet, SheetRadioList } from '@/components/ui/sheet';
import { useNarrowViewport } from '@/lib/mobile';
import {
  Menu,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from '@/components/ui/menu';
import { ErrorState, LoadingState } from '../primitives';
import type { ShareRole, SharePanelVM } from './types';

// The Artifact share panel: who can see it, the owner-snapshot sentence and
// Unpublish. Owners change a role from a menu that explains each role; everyone
// else sees plain text. Unpublish asks first. `accessSlot` is the extension
// point where PR 2 mounts T2's read-only summary of how viewers reach company
// systems (G11); PR 1 Artifacts leave it empty and nothing renders.

export interface SharePanelProps {
  vm: SharePanelVM;
  accessSlot?: ReactNode;
  onRoleChange?: (memberId: string, role: ShareRole) => void;
  /** The exact publication being unpublished, for the pointer compare-and-swap. */
  onUnpublish?: (publication: NonNullable<SharePanelVM['publication']>) => void;
  onRetry?: () => void;
}

const ROLES: [ShareRole, string, string][] = [
  ['view', 'shareRoleView', 'shareRoleViewMeaning'],
  ['edit', 'shareRoleEdit', 'shareRoleEditMeaning'],
  ['publish', 'shareRolePublish', 'shareRolePublishMeaning'],
];
const ROLE_NAME: Record<ShareRole, string> = {
  view: 'shareRoleView',
  edit: 'shareRoleEdit',
  publish: 'shareRolePublish',
};

export function SharePanel({
  vm,
  accessSlot,
  onRoleChange,
  onUnpublish,
  onRetry,
}: SharePanelProps) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  // The confirmation is bound to the exact publication it was opened for.
  const [confirming, setConfirming] = useState<NonNullable<SharePanelVM['publication']> | null>(
    null,
  );
  const publication = vm.publication;
  const live = vm.state === 'shared' && publication !== null && vm.publishedNumber !== null;
  const same = (
    a: NonNullable<SharePanelVM['publication']> | null,
    b: NonNullable<SharePanelVM['publication']> | null,
  ) =>
    a !== null &&
    b !== null &&
    a.artifactId === b.artifactId &&
    a.versionId === b.versionId &&
    a.generation === b.generation;
  // Any change of artifact, published version or pointer generation discards it.
  const confirmValid = live && vm.canManage && same(confirming, publication);
  useEffect(() => {
    if (confirming !== null && !confirmValid) setConfirming(null);
  }, [confirming, confirmValid]);
  return (
    <section className="ap-panel" aria-labelledby="ap-share-title" data-panel="share">
      <header className="ap-head">
        <h2 id="ap-share-title">{copy.shareTitle}</h2>
      </header>
      {vm.load === 'loading' && <LoadingState label={copy.shareLoading} />}
      {vm.load === 'error' && <ErrorState message={copy.shareErrorBody} onRetry={onRetry} />}
      {vm.load === 'ready' && (
        <>
          <p role="status">
            {vm.state === 'private'
              ? copy.sharePrivate
              : vm.state === 'revoked'
                ? copy.shareRevoked
                : formatCopy(copy.shareShared, { audience: vm.audienceLabel })}
          </p>
          {vm.state === 'shared' && <p className="ap-note">{copy.shareOwnerSnapshot}</p>}
          {vm.state === 'shared' && vm.members.length > 0 && (
            <ul className="ap-list">
              {vm.members.map((member) => (
                <li key={member.id} className="ap-row">
                  <div className="ap-row-copy">
                    <strong>{member.label}</strong>
                  </div>
                  {vm.canManage ? (
                    <RoleMenu
                      name={member.label}
                      role={member.role}
                      onChange={(role) => onRoleChange?.(member.id, role)}
                    />
                  ) : (
                    <span className="ap-badge">
                      {copy[ROLE_NAME[member.role] as keyof typeof copy]}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {live && (
            <p>{formatCopy(copy.shareLivePublished, { number: String(vm.publishedNumber) })}</p>
          )}
          {live && vm.canManage && !confirmValid && (
            <Button
              type="button"
              variant="ghost"
              className="ap-danger-text"
              onClick={() => setConfirming(publication)}
            >
              {copy.shareUnpublish}
            </Button>
          )}
          {confirmValid && (
            <Callout
              tone="warning"
              actions={
                <>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      // Guard against the current model, not the click that opened it.
                      if (!confirmValid || confirming === null) return;
                      setConfirming(null);
                      onUnpublish?.(confirming);
                    }}
                  >
                    {copy.shareUnpublish}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirming(null)}
                  >
                    {copy.shareUnpublishCancel}
                  </Button>
                </>
              }
            >
              {copy.shareUnpublishConfirm}
            </Callout>
          )}
          {!vm.canManage && <p className="ap-note">{copy.shareManageDenied}</p>}
          {accessSlot && (
            <div className="ap-slot" data-slot="access">
              {accessSlot}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function RoleMenu({
  name,
  role,
  onChange,
}: {
  name: string;
  role: ShareRole;
  onChange: (role: ShareRole) => void;
}) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const narrow = useNarrowViewport();
  const [open, setOpen] = useState(false);
  // Crossing the phone breakpoint never carries an open sheet across: closed on every change.
  useEffect(() => setOpen(false), [narrow]);
  const trigger = useRef<HTMLButtonElement>(null);
  const label = formatCopy(copy.shareRoleMenu, { name });
  const button = (
    <Button
      ref={trigger}
      type="button"
      variant="secondary"
      size="sm"
      aria-label={label}
      aria-haspopup={narrow ? 'dialog' : undefined}
      onClick={narrow ? () => setOpen(true) : undefined}
    >
      {copy[ROLE_NAME[role] as keyof typeof copy]}
      <ChevronDownIcon aria-hidden="true" />
    </Button>
  );
  // A phone gets the kit bottom sheet (titled, scrim, handle); a pointer screen keeps the shared
  // menu primitive, which supplies focus, arrow keys, Escape and return focus.
  if (narrow)
    return (
      <>
        {button}
        <BottomSheet
          open={open}
          onOpenChange={setOpen}
          title={copy.shareAccessSheetTitle}
          returnFocus={trigger}
        >
          <SheetRadioList
            label={label}
            choices={ROLES.map(([value, nameKey, meaningKey]) => ({
              value,
              label: copy[nameKey as keyof typeof copy],
              description: copy[meaningKey as keyof typeof copy],
            }))}
            value={role}
            onChoose={(value) => {
              setOpen(false);
              if (value !== role) onChange(value as ShareRole);
            }}
          />
        </BottomSheet>
      </>
    );
  return (
    <Menu>
      <MenuTrigger asChild>{button}</MenuTrigger>
      <MenuContent label={label}>
        <MenuRadioGroup
          value={role}
          onValueChange={(value) => {
            if (value !== role) onChange(value as ShareRole);
          }}
        >
          {ROLES.map(([value, nameKey, meaningKey]) => (
            <MenuRadioItem
              key={value}
              value={value}
              description={copy[meaningKey as keyof typeof copy]}
            >
              {copy[nameKey as keyof typeof copy]}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}
